import { Site, SiteContextService } from './../site-context.service';
import { IdentityService } from './../identity/identity.service';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Logger, LoggerFactory } from '../logger.service';
import { ExpiredAccountAccessService } from './expired-account-access.service';

@Injectable()
export class ExpiredAccountGuard {
  private readonly logger: Logger;
  private readonly expiredAccountAccess = inject(ExpiredAccountAccessService);

  constructor(
    loggerFactory: LoggerFactory,
    protected readonly identityService: IdentityService,
    protected readonly router: Router,
    private readonly siteContext: SiteContextService,
  ) {
    this.logger = loggerFactory.get('ExpiredAccountGuard');
  }

  public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const currentSite = await this.siteContext.getCurrentSiteAsync();

    if (!currentSite) {
      this.logger.warn('No site found, cannot evaluate whether the account is expired');
      return false;
    }

    return this.isAllowedOnExpiredAccount(currentSite, state.url);
  }

  public async canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const currentSite = await this.siteContext.getCurrentSiteAsync();
    const currentIdentity = this.identityService.currentIdentity;

    if (!currentIdentity) {
      this.logger.warn(`No identity found for visitor`);
      return false;
    }

    if (!currentSite) {
      this.logger.warn(`No site found for user '${currentIdentity.email}'`);
      return false;
    }

    if (this.isAllowedOnExpiredAccount(currentSite, state.url)) {
      return true;
    }

    if (state.url.includes('/campaigns/templates') || state.url.includes('/campaigns/export')) {
      return true;
    }
    this.router.navigate(['account-expired'], {
      skipLocationChange: true, // minimal effect. see https://github.com/angular/angular/issues/17004
      queryParams: {
        url: state.url,
        b: new Date().getTime(),
      },
    });
    return false;
  }

  // Blocks expired and paused accounts; a superadmin's per-site unlock is re-checked against the role on every read.
  private isAllowedOnExpiredAccount(site: Site, url: string): boolean {
    if ((!site.isExpired && !site.isPaused) || url.indexOf('/settings/plan-billing') !== -1) {
      return true;
    }
    return this.expiredAccountAccess.isUnlocked(site.id);
  }
}
